import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './AllSellStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import apiService from '../../services/api';
import ViewStockAdjustmentModal from './ViewStockAdjustmentModal';

const StockAdjustmentReport = () => {
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [adjustments, setAdjustments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalAdjustments, setTotalAdjustments] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    // Summary metrics state
    const [summary, setSummary] = useState({
        totalNormal: 0,
        totalAbnormal: 0,
        totalStockAdjustment: 0,
        totalAmountRecovered: 0
    });

    const fetchAdjustmentData = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.getStockAdjustments({
                page: currentPage,
                per_page: entriesPerPage,
                search: searchTerm
            });
            const data = response.data || response;
            const adjustmentList = Array.isArray(data) ? data : (data.data || []);
            setAdjustments(adjustmentList);
            setTotalAdjustments(data.total || adjustmentList.length);

            // Calculate summaries based on visible data or fetch from report API if available
            // For now, let's calculate from the list (though in production a specific report endpoint would be better)
            let normal = 0, abnormal = 0, total = 0, recovered = 0;
            adjustmentList.forEach(adj => {
                const amount = parseFloat(adj.final_total || adj.totalAmount || 0);
                const rec = parseFloat(adj.amount_recovered || adj.amountRecovered || 0);
                const type = (adj.adjustment_type || adj.type || '').toLowerCase();
                
                if (type === 'normal') normal += amount;
                else if (type === 'abnormal') abnormal += amount;
                
                total += amount;
                recovered += rec;
            });

            setSummary({
                totalNormal: normal,
                totalAbnormal: abnormal,
                totalStockAdjustment: total,
                totalAmountRecovered: recovered
            });

        } catch (err) {
      /* console.error('Failed to fetch report data', err); */
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAdjustmentData();
    }, [currentPage, entriesPerPage]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchAdjustmentData();
    };

    const handleExport = (format) => {
        const headers = ['Date', 'Reference No', 'Location', 'Adjustment type', 'Total Amount', 'Total amount recovered', 'Reason', 'Added By'];
        const rows = adjustments.map(adj => [
            adj.transaction_date || adj.date,
            adj.ref_no || adj.refNo,
            adj.business_location?.name || adj.location || 'Main Location',
            adj.adjustment_type || adj.type,
            (parseFloat(adj.final_total || adj.totalAmount || 0)).toFixed(2),
            (parseFloat(adj.amount_recovered || adj.amountRecovered || 0)).toFixed(2),
            adj.additional_notes || adj.reason || '',
            adj.created_by_name || adj.addedBy || 'N/A'
        ]);

        const title = 'Stock Adjustment Report';
        const filename = 'stock_adjustment_report';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();

        setShowExportDropdown(false);
    };

    const handleViewDetails = (adjustment) => {
        setSelectedAdjustment(adjustment);
        setShowViewModal(true);
    };

    const formatCurrency = (amount) => `$ ${parseFloat(amount).toFixed(2)}`;

    return (
        <div style={{ paddingTop: '0.5rem' }}>
            {/* ── Summary Cards ── */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
            }}>
                {/* Left card */}
                <div style={{
                    background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)',
                    borderRadius: '10px', padding: '1.25rem 1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                    <div className="report-metric-row">
                        <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>Total Normal:</span>
                        <strong style={{ color: 'var(--etpos-text, #1e293b)' }}>{formatCurrency(summary.totalNormal)}</strong>
                    </div>
                    <div className="report-metric-row">
                        <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>Total Abnormal:</span>
                        <strong style={{ color: 'var(--etpos-text, #1e293b)' }}>{formatCurrency(summary.totalAbnormal)}</strong>
                    </div>
                    <div className="report-metric-row total">
                        <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>Total Stock Adjustment:</span>
                        <strong style={{ color: 'var(--etpos-text, #1e293b)' }}>{formatCurrency(summary.totalStockAdjustment)}</strong>
                    </div>
                </div>

                {/* Right card */}
                <div style={{
                    background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)',
                    borderRadius: '10px', padding: '1.25rem 1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <div style={{ width: '100%' }}>
                        <div className="report-metric-row large">
                            <span style={{ color: 'var(--etpos-text-muted, #64748b)' }}>Total Amount Recovered:</span>
                            <strong style={{ color: '#10b981', fontSize: '1.25rem' }}>{formatCurrency(summary.totalAmountRecovered)}</strong>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Stock Adjustments Table ── */}
            <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

                {/* Table title */}
                <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '700', fontSize: '1rem', color: 'var(--etpos-text, #1e293b)' }}>Stock Adjustments</span>
                    {isLoading && <div className="small-spinner"></div>}
                </div>

                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1rem 1.25rem', position: 'relative', zIndex: 20 }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>

                    <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {/* Export Button */}
                        <div className="export-dropdown-container" style={{ position: 'relative', zIndex: 100 }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', zIndex: 9999, minWidth: '160px' }}>
                                    {[
                                        { label: 'Export CSV', action: () => handleExport('CSV'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
                                        { label: 'Export Excel', action: () => handleExport('Excel'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h8"/><path d="M8 17h8"/></svg> },
                                        { label: 'Print', action: () => handleExport('Print'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg> },
                                        { label: 'Export PDF', action: () => handleExport('PDF'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15v-6h2a2 2 0 0 1 0 4H9"/></svg> }
                                    ].map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.55rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--etpos-bg, #f1f5f9)'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            {item.icon}
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSearchSubmit}>
                            <input
                                type="text"
                                placeholder="Search ..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    padding: '0.4rem 0.75rem',
                                    border: '1px solid var(--etpos-border, #e2e8f0)',
                                    borderRadius: '6px',
                                    fontSize: '0.8125rem',
                                    outline: 'none',
                                    width: '180px',
                                }}
                            />
                        </form>
                    </div>
                </div>

                {/* Table */}
                <div className="products-table-container">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>Action</th>
                                <th>Date</th>
                                <th>Reference No</th>
                                <th>Location</th>
                                <th>Adjustment type</th>
                                <th>Total Amount</th>
                                <th>Total amount recovered</th>
                                <th>Reason</th>
                                <th>Added By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {adjustments.length === 0 ? (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                                        {isLoading ? 'Loading data...' : 'No data available in table'}
                                    </td>
                                </tr>
                            ) : (
                                adjustments.map((adj) => (
                                    <tr key={adj.id}>
                                        <td>
                                            <button 
                                                className="action-btn-view" 
                                                onClick={() => handleViewDetails(adj)}
                                                style={{ padding: '0.25rem 0.75rem', borderRadius: '4px', background: '#f1f5f9', border: '1px solid var(--etpos-border, #e2e8f0)', fontSize: '0.75rem', cursor: 'pointer' }}
                                            >
                                                View
                                            </button>
                                        </td>
                                        <td>{adj.transaction_date || adj.date}</td>
                                        <td style={{ fontWeight: '700' }}>{adj.ref_no || adj.refNo}</td>
                                        <td>{adj.business_location?.name || adj.location || 'Main Location'}</td>
                                        <td>
                                            <span className={`adjustment-type-badge ${(adj.adjustment_type || adj.type || '').toLowerCase()}`}>
                                                {adj.adjustment_type || adj.type}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: '700' }}>{formatCurrency(adj.final_total || adj.totalAmount || 0)}</td>
                                        <td>{formatCurrency(adj.amount_recovered || adj.amountRecovered || 0)}</td>
                                        <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={adj.additional_notes || adj.reason}>
                                            {adj.additional_notes || adj.reason || '--'}
                                        </td>
                                        <td>{adj.created_by_name || adj.addedBy || 'N/A'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem' }}>
                        Showing {adjustments.length > 0 ? ((currentPage - 1) * entriesPerPage) + 1 : 0} to {Math.min(currentPage * entriesPerPage, totalAdjustments)} of {totalAdjustments} entries
                    </div>
                    <div className="pagination-controls">
                        <button 
                            className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                        >Previous</button>
                        <button 
                            className={`page-btn ${currentPage * entriesPerPage >= totalAdjustments ? 'disabled' : ''}`}
                            onClick={() => currentPage * entriesPerPage < totalAdjustments && setCurrentPage(currentPage + 1)}
                        >Next</button>
                    </div>
                </div>
            </div>

            {showViewModal && <ViewStockAdjustmentModal adjustment={selectedAdjustment} onClose={() => setShowViewModal(false)} />}

            <style>{`
                .report-metric-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.6rem 0;
                    border-bottom: 1px solid var(--etpos-border, #f1f5f9);
                }
                .report-metric-row:last-child {
                    border-bottom: none;
                }
                .report-metric-row.total {
                    margin-top: 0.5rem;
                    padding-top: 1rem;
                    border-top: 2px solid var(--etpos-border, #e2e8f0);
                }
                .report-metric-row span {
                    font-weight: 500;
                    font-size: 0.9rem;
                }
                .report-metric-row strong {
                    font-weight: 700;
                    font-size: 1rem;
                }
                .small-spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #f3f3f3;
                    border-top: 2px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .adjustment-type-badge {
                    padding: 0.2rem 0.6rem;
                    border-radius: 999px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    text-transform: uppercase;
                }
                .adjustment-type-badge.normal {
                    background: #dcfce7;
                    color: #166534;
                }
                .adjustment-type-badge.abnormal {
                    background: #fee2e2;
                    color: #991b1b;
                }
            `}</style>
        </div>
    );
};

export default StockAdjustmentReport;
